import {readSourceSlide,validateSourceSlides,type SourceSlide,type SourceSlideId} from '../../protocol/source-slide';
import {SLIDE_ANCHORS,SLIDE_ARRAY_CAPS,SLIDE_TEXT_FIELDS_BY_TYPE,normalizeSlideField} from '../../../../src/lib/templates/slide-edit-contract';
import {canSwitchSlideType,convertSlideContent} from '../../../../src/lib/templates/slide-type-convert';
import {canKineticTypo,MOTION_TOGGLE_TYPES} from '../../../../src/lib/templates/slide-kind-contract';
import type {UndoableState,EditorState} from '../../vendor/src/features/remotion-editor/editor/state/types';
import type {EditorStarterItem} from '../../vendor/src/features/remotion-editor/editor/items/item-type';
import {itemOwner} from './group-split';
import {hasPendingTimelineEdit} from './edit-ownership';
export const sourceSlideEditSignature=(item:EditorStarterItem)=>JSON.stringify([item.sourceSlide,'assetId' in item?item.assetId:null,item.sourceScene]);
function editable(document:UndoableState,id:string,expected:string){
 const item=document.items[id],data=readSourceSlide(item?.sourceSlide);
 if(!item||item.type!=='image'||!data||!data.slide||sourceSlideEditSignature(item)!==expected)throw Error('장표가 바뀌었습니다. 다시 선택해 주세요.');
 if(data.provenance.editing!=='content')throw Error(data.provenance.derived==='brief-filler'?'이 장표 문장은 씬 대본에서 가져옵니다. 대본에서 수정해 주세요.':'이 원본의 장표 문구 편집은 지원하지 않습니다.');
 const owner=itemOwner({undoableState:document},id);if(!owner)throw Error('문서에서 장표를 다시 선택해 주세요.');
 if(owner.track.locked)throw Error('장표 트랙 잠금을 해제해 주세요.');
 return {item,data,slide:data.slide};
}
function write(document:UndoableState,id:string,data:SourceSlide):UndoableState{
 const next={...document,items:{...document.items,[id]:{...document.items[id],sourceSlide:data}}};
 if(!validateSourceSlides(next))throw Error('장표와 이미지 자산 연결을 확인해 주세요.');
 return next;
}
const sameValue=(a:unknown,b:unknown)=>JSON.stringify(a)===JSON.stringify(b);
const row=(v:unknown):Record<string,unknown>=>v&&typeof v==='object'&&!Array.isArray(v)?v as Record<string,unknown>:{};
/** Authoring caps limit additions, not imported content that the renderer still displays. */
function boundedArray(value:unknown,previous:unknown,cap:number):unknown[]{
 if(!Array.isArray(value))throw Error('장표 항목을 다시 확인해 주세요.');
 const limit=Math.max(cap,Array.isArray(previous)?previous.length:0);
 if(value.length>limit)throw Error(`새 항목은 최대 ${cap}개까지 입력할 수 있습니다.`);
 return value;
}
function editRow(value:unknown,previous:unknown,keys:readonly string[],kind:'cards'|'stats'|'bad'){
 if(sameValue(value,previous))return structuredClone(previous);
 const input=row(value),old=row(previous);
 const normalized=kind==='bad'?row(normalizeSlideField('bad',input)):row((normalizeSlideField(kind,[input]) as unknown[])[0]);
 const result={...old,...normalized};
 for(const key of keys){
  if(sameValue(input[key],old[key])){if(Object.prototype.hasOwnProperty.call(old,key))result[key]=structuredClone(old[key]);else delete result[key];continue;}
  if(key==='body'){
   const body=boundedArray(input.body??[],old.body,SLIDE_ARRAY_CAPS.body).map(v=>normalizeSlideField('title',v));
   if(body.length)result.body=body;else delete result.body;
  }else if(Object.prototype.hasOwnProperty.call(normalized,key))result[key]=normalized[key];else delete result[key];
 }
 return result;
}
function normalizeSourceSlideEdit(key:string,value:unknown,previous:unknown):unknown{
 if(sameValue(value,previous))return previous;
 if(key==='bad'||key==='good')return editRow(value,previous,['title','body'],'bad');
 if(key==='cards'||key==='stats'){
  const values=boundedArray(value,previous,SLIDE_ARRAY_CAPS[key]),old=Array.isArray(previous)?previous:[];
  return values.map((v,index)=>{
   const unchanged=old.find(prior=>sameValue(v,prior));
   if(unchanged!==undefined)return structuredClone(unchanged);
   return editRow(v,old.length===values.length?old[index]:undefined,key==='cards'?['badge','title','body']:['value','label'],key);
  });
 }
 if(key==='items'||key==='steps')return boundedArray(value,previous,SLIDE_ARRAY_CAPS[key]).map(v=>normalizeSlideField('title',v));
 return normalizeSlideField(key,value);
}
export function editSourceSlide(document:UndoableState,id:string,expected:string,patch:Record<string,unknown>):UndoableState{
 const {data,slide}=editable(document,id,expected),content={...slide.content};
 for(const key of Object.keys(patch)){
  if(key==='anchor'){if(data.context.motionBrief&&slide.type!=='posterStack')throw Error('무대 장표는 가운데로 배치합니다. 장표 전체 위치는 기존 속성에서 조정해 주세요.');const anchor=SLIDE_ANCHORS.find(a=>a===patch[key]);if(!anchor)throw Error('장표 정렬을 다시 선택해 주세요.');content.anchor=anchor;continue;}
  if(!SLIDE_TEXT_FIELDS_BY_TYPE[slide.type].includes(key))throw Error('이 장표에서 편집할 수 없는 항목입니다.');
  content[key]=normalizeSourceSlideEdit(key,patch[key],slide.content[key]);
 }
 if(JSON.stringify(content)===JSON.stringify(slide.content))return document;
 return write(document,id,{...data,slide:{...slide,content}});
}
export type SourceSlideConversion={observed:UndoableState;id:string;expected:string;result:SourceSlide;dropped:string[]};
export function reviewSourceSlideConversion(document:UndoableState,id:string,target:SourceSlideId):SourceSlideConversion{
 const expected=sourceSlideEditSignature(document.items[id]),{data,slide}=editable(document,id,expected);
 let content:Record<string,unknown>,dropped:string[];
 if(canSwitchSlideType(slide.type,target)){const result=convertSlideContent(slide.type,target,slide.content)!;content=result.content;dropped=result.dropped;}
 else if(slide.type!==target&&MOTION_TOGGLE_TYPES.has(slide.type)&&MOTION_TOGGLE_TYPES.has(target)){
  if(target==='kineticTypo'&&!canKineticTypo(typeof slide.content.title==='string'?slide.content.title.trim():''))throw Error('모션 타이포 제목은 한 줄, 24자 이내로 입력해 주세요.');
  content={...slide.content};dropped=[];
 }else throw Error('내용 구조가 달라 이 종류로 바꿀 수 없습니다.');
 return {observed:document,id,expected,result:{...data,slide:{type:target,content}},dropped};
}
export function applySourceSlideConversion(state:EditorState,review:SourceSlideConversion,confirmLoss:boolean):EditorState{
 if(state.undoableState!==review.observed||!state.selectedItems.includes(review.id)||hasPendingTimelineEdit(state))throw Error('장표 또는 문서가 바뀌었습니다. 미리보기를 다시 확인해 주세요.');
 editable(state.undoableState,review.id,review.expected);
 if(review.dropped.length&&!confirmLoss)throw Error('빠지는 내용을 확인하고 적용해 주세요.');
 // Apply the exact reviewed result; no independent conversion or asynchronous overwrite.
 return {...state,undoableState:write(state.undoableState,review.id,structuredClone(review.result))};
}
